import React, { Dispatch, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import { CallStatus } from '../../../lib/enums';
import lang from '../../../lib/language';
import { RootState } from '../../../lib/store';
import { setWebLoginReady } from '../../../lib/store/app';
import { checkCookieSession as checkCookieSessionCall } from '../../../lib/store/common-actions';
import {
  activateDemoAccount,
  updateIndividualTransitionFromNewToForVerification as updateIndividualTransitionFromNewToForVerificationCall,
} from '../../../lib/store/crm';
import { getSelectedTradingPlan, isPlanDemoType } from '../../../lib/store/crm/helpers';
import { CRMCall } from '../../../lib/store/crm/types';
import { hasValidCookie, isUserBeforeForVerificationStatus } from '../../../lib/store/selectors';
import { isCallStatusReady } from '../../../lib/util/error-handling/StatusByCallHelpers';

import config from '../../../config';
import {
  GTM_CATEGORIES,
  GTM_CLIENT_CREATION_STEPS,
  GTM_DEMO_ACCOUNT,
  GTM_EVENTS,
} from '../../../constants/gtm';
import EnrollmentState from '../../../store-util/EnrollmentState';
import { hashSHA256 } from '../../../util/hash';
import { UI_ROUTES } from '../../../util/routes';
import Layout from '../component/Layout';
import VerifiedPhoneModal from '../component/VerifiedPhoneModal/VerifiedPhoneModal';
import { PaymentMethodSelectionState } from '../PaymentMethodSelection/types';

import styles from './EnrollPhoneVerified.module.scss';

let hasGtmPhoneVerifiedFired = false;
let hasGtmDemoInitFired = false;

const EnrollPhoneVerified = (props: ReduxProps): JSX.Element => {
  const {
    isCookieValid,
    activateDemoPlanAccount,
    isAppLoaded,
    isUserStatusBeforeForVerification,
    updateIndividualTransitionFromNewToForVerification,
    selectedTradingPlanID,
    initiallySelectedAutoPass,
    isSelectedPlanDemoType,
    isStatusInForVerification,
    isPhoneConfirmed,
    isEmailConfirmed,
    isActivatedDemoAccount,
    setLoginReady,
    isWebLoginReady,
    checkCookieSession,
    userEmail,
    userId,
    selectedTradingPlan,
    referrerClientId,
  } = props;
  const navigate = useNavigate();
  const { form } = EnrollmentState.use();
  const isDemoOrNoPlan = isSelectedPlanDemoType || !selectedTradingPlanID;
  const isAceHubReferrer = referrerClientId === config.auth.aceHubClientId;
  const [awaitingForVerificationReady, setAwaitingForVerificationReady] = useState(false);
  const hasSubmittedContinue = form.steps.isVerifyPhoneContinueSubmitted;

  useEffect(() => {
    if (isEmailConfirmed) {
      EnrollmentState.setVerifyEmailStepPassed(true);
    }
    if (isPhoneConfirmed) {
      EnrollmentState.setVerifyPhoneStepPassed(true);
    }
  }, [isPhoneConfirmed, isEmailConfirmed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push GTM event for STEP_5 - phone verified (fires once when CRM data is available)
  useEffect(() => {
    if (isPhoneConfirmed && userEmail && !hasGtmPhoneVerifiedFired) {
      hasGtmPhoneVerifiedFired = true;
      const pushGtmEvent = async () => {
        const hashedEmail = await hashSHA256(userEmail);
        TagManager.dataLayer({
          dataLayer: {
            event: GTM_EVENTS.CLIENT_CREATION,
            event_step: GTM_CLIENT_CREATION_STEPS.STEP_5.step,
            event_category: GTM_CATEGORIES.CLIENT_CREATION_FUNNEL,
            event_action: GTM_CLIENT_CREATION_STEPS.STEP_5.action,
            event_label: GTM_CLIENT_CREATION_STEPS.STEP_5.label,
            client_id: userId,
            user_email_hashed: hashedEmail,
          },
        });
      };
      pushGtmEvent();
    }
  }, [isPhoneConfirmed, userEmail, userId]);

  useEffect(() => {
    if (isAppLoaded && !form.steps.isVerifyPhonePassed) {
      navigate(UI_ROUTES.enrollPhoneVerification, { replace: true });
    }
  }, [isAppLoaded, form.steps.isVerifyPhonePassed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSelectedPlanDemoType && isStatusInForVerification && !isActivatedDemoAccount) {
      if (!hasGtmDemoInitFired && userEmail) {
        hasGtmDemoInitFired = true;
        const pushDemoInitEvent = async () => {
          const hashedEmail = await hashSHA256(userEmail);
          TagManager.dataLayer({
            dataLayer: {
              event: GTM_EVENTS.DEMO_ACCOUNT_INITIATED,
              event_category: GTM_CATEGORIES.ACCOUNT_MANAGEMENT,
              event_action: GTM_DEMO_ACCOUNT.ACTION,
              event_label: GTM_DEMO_ACCOUNT.LABEL,
              client_id: userId,
              user_email_hashed: hashedEmail,
              demo_account_size: selectedTradingPlan?.daily_hard_bp,
            },
          });
        };
        pushDemoInitEvent();
      }
      activateDemoPlanAccount();
    }
  }, [isSelectedPlanDemoType, isStatusInForVerification, isActivatedDemoAccount]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateToPaymentSelection = useCallback(() => {
    EnrollmentState.setVerifyPhoneContinueSubmitted(false);
    const historyStateParams: PaymentMethodSelectionState = {
      planId: selectedTradingPlanID,
      prevPage: UI_ROUTES.products,
      isEnroll: true,
      withAutoPass: initiallySelectedAutoPass,
    };

    navigate(
      {
        pathname: UI_ROUTES.paymentMethodSelection,
      },
      { state: historyStateParams },
    );
  }, [selectedTradingPlanID, initiallySelectedAutoPass, navigate]);

  useLayoutEffect(() => {
    if (!hasSubmittedContinue || !isCookieValid) {
      return;
    }

    if (isAceHubReferrer) {
      EnrollmentState.setVerifyPhoneContinueSubmitted(false);
      redirectToHub();
      return;
    }

    if (!isWebLoginReady) {
      setLoginReady();
    }

    if (!selectedTradingPlanID || isActivatedDemoAccount) {
      EnrollmentState.setVerifyPhoneContinueSubmitted(false);
      navigate(UI_ROUTES.overview, { state: { skipPaymentEnrollment: true }, replace: true });
      return;
    }

    // Paid plan: the payment screen requires a valid cookie session, so continue only once
    // the session is valid and the for-verification transition is done. Both are kept outside
    // of the component state on purpose - validating the cookie flips the app to the
    // authenticated router, which remounts this screen and would drop any local state.
    if (!isDemoOrNoPlan && (!isUserStatusBeforeForVerification || isStatusInForVerification)) {
      navigateToPaymentSelection();
    }
  }, [
    hasSubmittedContinue,
    selectedTradingPlanID,
    isActivatedDemoAccount,
    isCookieValid,
    isWebLoginReady,
    isAceHubReferrer,
    isDemoOrNoPlan,
    isUserStatusBeforeForVerification,
    isStatusInForVerification,
    navigateToPaymentSelection,
    setLoginReady,
    navigate,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAppLoaded && hasSubmittedContinue && isActivatedDemoAccount) {
      EnrollmentState.setVerifyPhoneContinueSubmitted(false);
      if (isAceHubReferrer) {
        redirectToHub();
      } else {
        navigate(UI_ROUTES.overview);
      }
    }
  }, [isActivatedDemoAccount, hasSubmittedContinue]); // eslint-disable-line react-hooks/exhaustive-deps

  const redirectToHub = useCallback(() => {
    window.location.replace(config.links.aceHubUrl);
  }, []);

  const proceedAfterVerificationReady = useCallback(() => {
    // Allow proceeding without a plan (like mobile) or handle demo/selected plan
    if (isDemoOrNoPlan) {
      if (isAceHubReferrer) {
        EnrollmentState.setVerifyPhoneContinueSubmitted(false);
        redirectToHub();
        return;
      }
      if (isCookieValid && isWebLoginReady) {
        return;
      }
      if (!isCookieValid) {
        checkCookieSession();
      }
    } else {
      // continue with payment for the selected plan. The payment screen bounces the user out
      // when there is no valid cookie session yet, so wait for the session check to complete
      // first - the layout effect above resumes the navigation once the cookie is valid.
      if (!isCookieValid) {
        checkCookieSession();
        return;
      }

      navigateToPaymentSelection();
    }
  }, [
    isDemoOrNoPlan,
    isCookieValid,
    isWebLoginReady,
    checkCookieSession,
    navigateToPaymentSelection,
    isAceHubReferrer,
    redirectToHub,
  ]);

  useEffect(() => {
    if (awaitingForVerificationReady && isStatusInForVerification) {
      setAwaitingForVerificationReady(false);
      proceedAfterVerificationReady();
    }
  }, [awaitingForVerificationReady, isStatusInForVerification, proceedAfterVerificationReady]);

  return (
    <Layout
      transparentContent
      formTitle={''}
      contentStyle={styles.verificationContent}
      displayBackButton={false}
      displayFooter={false}
      displayNextButton
      nextButtonText={
        isDemoOrNoPlan ? lang.commonContinueButton() : lang.commonContinuePaymentButton()
      }
      displayLoginButton={false}
      onSubmit={() => {
        EnrollmentState.setVerifyPhoneStepPassed(true);
        EnrollmentState.setVerifyPhoneContinueSubmitted(true);
        if (isUserStatusBeforeForVerification) {
          updateIndividualTransitionFromNewToForVerification();
          // Wait for READY status before continuing
          setAwaitingForVerificationReady(true);
          return;
        }
        proceedAfterVerificationReady();
      }}
    >
      <VerifiedPhoneModal />
    </Layout>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  setLoginReady: () => dispatch(setWebLoginReady()),
  updateIndividualTransitionFromNewToForVerification: (): void =>
    dispatch(updateIndividualTransitionFromNewToForVerificationCall()),
  activateDemoPlanAccount: () => dispatch(activateDemoAccount()),
  checkCookieSession: () => dispatch(checkCookieSessionCall()),
});

const mapStateToProps = (state: RootState) => ({
  isCookieValid: hasValidCookie(state),
  isActivatedDemoAccount: isCallStatusReady(CRMCall.activateDemoAccount, state.crm.statusByCall),
  isAppLoaded: state.app.loaded,
  isWebLoginReady: state.app.isWebLoginReady,
  isPhoneConfirmed: state.crm.individualExtendedInfo?.phone_number_confirmed,
  isEmailConfirmed: state.crm.individualExtendedInfo?.email_confirmed,
  userEmail: state.crm.individualExtendedInfo?.email,
  userId: state.auth.enroll.userId,
  selectedTradingPlanID: getSelectedTradingPlan(state.crm)?.id,
  selectedTradingPlan: getSelectedTradingPlan(state.crm),
  initiallySelectedAutoPass: state.crm.enroll.initiallySelectedAutoPass,
  isUserStatusBeforeForVerification: isUserBeforeForVerificationStatus(
    state.crm.individualExtendedInfo,
  ),
  isSelectedPlanDemoType: isPlanDemoType(getSelectedTradingPlan(state.crm)?.id ?? 0, state.crm),
  referrerClientId: state.auth.enroll.referrerClientId,
  isStatusInForVerification:
    state.crm.statusByCall[CRMCall.updateIndividualTransitionFromNewToForVerification]?.status
    === CallStatus.READY,
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EnrollPhoneVerified);
